import { apiSlice } from '../api/apiSlice'
import {
  AutomatedCurationFilters,
  CurationPage,
  CreateHomepageSectionPayload,
  HomepageConfig,
  HomepageCurationType,
  HomepageSection,
  HomepageSectionStatus,
  TimeSlotGroupPayload,
} from './homePageV21.type'

type ApiEnvelope<T> = {
  success?: boolean
  data?: T
  message?: string
}

type FulfilmentCentreScope = {
  page?: CurationPage
  fulfilmentCentreId: string
}

const unwrap = <T>(response: ApiEnvelope<T> | T): T => {
  if (
    typeof response === 'object' &&
    response !== null &&
    'data' in response &&
    response.data !== undefined
  ) {
    return response.data
  }
  return response as T
}

export const goodHomeV21ApiSlice = apiSlice.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getHomepageCurationTypes: builder.query<HomepageCurationType[], void>({
      query: () => ({
        url: 'v2/good-homepage/curation',
        method: 'GET',
      }),
      providesTags: [{ type: 'CURATION_TYPE', id: 'LIST' }],
      transformResponse: unwrap<HomepageCurationType[]>,
    }),
    createHomepageCurationType: builder.mutation<
      HomepageCurationType,
      Pick<HomepageCurationType, 'name' | 'slug' | 'productSection'>
    >({
      query: (body) => ({
        url: 'v2/good-homepage/curation',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CURATION_TYPE', id: 'LIST' }],
      transformResponse: unwrap<HomepageCurationType>,
    }),
    getCurationSections: builder.query<
      HomepageConfig,
      { curationTypeId: string } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/sections`,
        method: 'GET',
        params: { fulfilmentCentreId, page },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
      transformResponse: unwrap<HomepageConfig>,
    }),
    previewCurationKitchens: builder.query<
      { items: HomepageSection['items']; count: number },
      { curationTypeId: string } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/kitchens-near-you/preview`,
        params: { fulfilmentCentreId, page },
      }),
      providesTags: ['VENDOR'],
      transformResponse: unwrap<{
        items: HomepageSection['items']
        count: number
      }>,
    }),
    previewCurationAutomated: builder.query<
      { items: HomepageSection['items']; count: number },
      {
        curationTypeId: string
        filters: AutomatedCurationFilters
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, filters, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/automated-curation/preview`,
        method: 'POST',
        body: filters,
        params: { fulfilmentCentreId, page },
      }),
      providesTags: ['PRODUCT'],
      transformResponse: unwrap<{
        items: HomepageSection['items']
        count: number
      }>,
    }),
    previewCurationSubcategory: builder.query<
      {
        items: HomepageSection['items']
        count: number
        subcategory: Record<string, any> | null
        source: 'OUR_PICKS' | 'ALL_PRODUCTS' | null
      },
      {
        curationTypeId: string
        subcategoryId: string
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, subcategoryId, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/subcategory-curation/preview`,
        method: 'POST',
        body: { subcategory: subcategoryId },
        params: { fulfilmentCentreId, page },
      }),
      providesTags: ['PRODUCT'],
      transformResponse: unwrap<{
        items: HomepageSection['items']
        count: number
        subcategory: Record<string, any> | null
        source: 'OUR_PICKS' | 'ALL_PRODUCTS' | null
      }>,
    }),
    previewCurationCategory: builder.query<
      {
        items: HomepageSection['items']
        count: number
        category: Record<string, any> | null
        source: 'OUR_PICKS' | 'ALL_PRODUCTS' | null
      },
      {
        curationTypeId: string
        categoryId: string
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, categoryId, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/category-curation/preview`,
        method: 'POST',
        body: { category: categoryId },
        params: { fulfilmentCentreId, page },
      }),
      providesTags: ['PRODUCT'],
      transformResponse: unwrap<{
        items: HomepageSection['items']
        count: number
        category: Record<string, any> | null
        source: 'OUR_PICKS' | 'ALL_PRODUCTS' | null
      }>,
    }),
    createCurationSection: builder.mutation<
      HomepageSection,
      {
        curationTypeId: string
        payload: CreateHomepageSectionPayload
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, payload, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/sections`,
        method: 'POST',
        body: payload,
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
      transformResponse: unwrap<HomepageSection>,
    }),
    updateCurationSection: builder.mutation<
      HomepageSection,
      {
        curationTypeId: string
        sectionId: string
        payload: Partial<CreateHomepageSectionPayload> & {
          status?: HomepageSectionStatus
        }
      } & FulfilmentCentreScope
    >({
      query: ({
        curationTypeId,
        sectionId,
        payload,
        fulfilmentCentreId,
        page,
      }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/sections/${sectionId}`,
        method: 'PUT',
        body: payload,
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.sectionId },
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
      transformResponse: unwrap<HomepageSection>,
    }),
    deleteCurationSection: builder.mutation<
      boolean,
      { curationTypeId: string; sectionId: string } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, sectionId, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/sections/${sectionId}`,
        method: 'DELETE',
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.sectionId },
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
      transformResponse: unwrap<boolean>,
    }),
    reorderCurationSections: builder.mutation<
      { success: boolean },
      {
        curationTypeId: string
        sectionIds: string[]
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, sectionIds, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/sections/reorder`,
        method: 'PATCH',
        body: { sectionIds },
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
    }),
    publishCurationSections: builder.mutation<
      { success: boolean },
      {
        curationTypeId: string
        sectionIds?: string[]
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, sectionIds, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/publish`,
        method: 'POST',
        body: sectionIds ? { sectionIds } : {},
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
        { type: 'HOMEPAGE_SECTION', id: 'LIST' },
      ],
    }),
    createCurationTimeSlotGroup: builder.mutation<
      {
        groupName: string
        updatedSections: HomepageSection[]
        message: string
      },
      {
        curationTypeId: string
        payload: TimeSlotGroupPayload
      } & FulfilmentCentreScope
    >({
      query: ({ curationTypeId, payload, fulfilmentCentreId, page }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/time-slot-groups`,
        method: 'POST',
        body: payload,
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
      transformResponse: unwrap<{
        groupName: string
        updatedSections: HomepageSection[]
        message: string
      }>,
    }),
    updateCurationTimeSlotGroup: builder.mutation<
      {
        groupName: string
        updatedSections: HomepageSection[]
        message: string
      },
      {
        curationTypeId: string
        groupName: string
        payload: TimeSlotGroupPayload
      } & FulfilmentCentreScope
    >({
      query: ({
        curationTypeId,
        groupName,
        payload,
        fulfilmentCentreId,
        page,
      }) => ({
        url: `v2/good-homepage/curation/${curationTypeId}/time-slot-groups/${encodeURIComponent(
          groupName,
        )}`,
        method: 'PUT',
        body: payload,
        params: { fulfilmentCentreId, page },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.curationTypeId },
      ],
      transformResponse: unwrap<{
        groupName: string
        updatedSections: HomepageSection[]
        message: string
      }>,
    }),
  }),
})

export const {
  useGetHomepageCurationTypesQuery,
  useCreateHomepageCurationTypeMutation,
  useGetCurationSectionsQuery,
  usePreviewCurationAutomatedQuery,
  usePreviewCurationKitchensQuery,
  usePreviewCurationSubcategoryQuery,
  usePreviewCurationCategoryQuery,
  useCreateCurationSectionMutation,
  useUpdateCurationSectionMutation,
  useDeleteCurationSectionMutation,
  useReorderCurationSectionsMutation,
  usePublishCurationSectionsMutation,
  useCreateCurationTimeSlotGroupMutation,
  useUpdateCurationTimeSlotGroupMutation,
} = goodHomeV21ApiSlice
